/**
 * A-W4-WEB1 (W4 Schritt 3+4) — der Zugriff kommt vom Server.
 *
 * Bis W4 spiegelte diese Datei die Rollentabelle aus
 * `services/api/src/entitlements.ts` als generierten Katalog im Client-Bundle
 * und rechnete Rollen selbst in Rechte um. Genau diese Pflegepflicht loest
 * `docs/design/W4_ZUGRIFF_INVENTAR.md` §7/§8 auf: `GET /v1/me/access` liefert
 * die **gerechnete** Rechtemenge, das Rollen-Label fuer die Chips und die
 * Modulsicht des Akteurs. `roles` ist in der Antwort bewusst NICHT enthalten,
 * damit kein `roles.includes(...)` nachwaechst.
 *
 * Die Signatur von `useAccess()`/`useOptionalAccess()` bleibt `{ can, ... }`:
 * damit sind die 52 Aufrufstellen ohne eine einzige Aenderung umgestellt.
 *
 * Fail closed (§7.5): kein Provider, laufende erste Abfrage oder Fehler =
 * leere Rechtemenge. Die Richtung ist die sichere — ein Knopf fehlt, statt
 * einen zu zeigen, den der Server danach mit 403 abweist. Der Server bleibt
 * der Entscheider; dieser Vertrag versteckt nur Knoepfe.
 */
import { createContext, useContext, useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { PERMISSIONS } from "@genius-tracker/api-client/permission-catalog";
import type { BillingModule, ModuleState } from "../modules/moduleMap.js";
import { useOptionalSession, useSession } from "../session/SessionProvider.js";
import { apiGet } from "./api.js";
import { accessTestBridge } from "./accessTestBridge.js";

/** Every catalogued permission, not only those a built-in role carries (same as admin). */
export type Permission = (typeof PERMISSIONS)[number];

/** Ein Modulzustand aus der Zugriffsantwort — gleiche Form wie `/v1/me/modules`. */
export interface AccessModuleState {
  readonly module: BillingModule;
  readonly state: ModuleState;
}

/**
 * A-GI-STUFE2-KACHEL-ZUGANG1 (GT-OPEN-154.2) — die Zugangslage eines
 * zweistufig gesperrten Features. Die Antworten, die der Produktpfad schon
 * ueber HTTP gibt: `feature_unavailable` ist der `503` des
 * Plattform-Master-Flags, `not_entitled` der `402` der Mandanten-Berechtigung.
 * Die Namen sind die der Drahtfassung, damit die Oberflaeche keine zweite
 * Vokabel fuer dieselbe Lage lernt.
 *
 * Review I 002989: `source_error` heisst "die Quelle dieses Features ist nicht
 * lesbar" — ein eigener Zustand, weil der Server dafuer nicht mehr die ganze
 * Antwort fallen laesst (siehe
 * `services/api/src/modules/billing-modules/me-access.service.ts`). Wie jeder
 * Wert ausser `available` gibt er keinen Zugang frei.
 */
export type FeatureAvailability =
  | "available"
  | "feature_unavailable"
  | "not_entitled"
  | "source_error";

/** Ein Feature-Zugang aus der Zugriffsantwort. */
export interface AccessFeatureState {
  readonly feature: string;
  readonly availability: FeatureAvailability;
}

/**
 * Die Werte, die dieser Client kennt. Ein neuerer Server kann einen weiteren
 * senden; der ist dann geschlossen (siehe {@link availabilityOfFeature}) und
 * darf die Oberflaeche nicht in einen Zustand ohne Darstellung fuehren.
 */
const KNOWN_AVAILABILITIES: readonly string[] = [
  "available",
  "feature_unavailable",
  "not_entitled",
  "source_error",
];

/** Der Vertrag von `GET /v1/me/access` (W4 §7). */
export interface MeAccessResponse {
  readonly actorId: string;
  readonly tenantId: string;
  readonly permissions: readonly Permission[];
  readonly roleLabel: string | null;
  readonly modules: readonly AccessModuleState[];
  /**
   * GT-OPEN-154.2: die Zugangslage gesperrter Features. Optional gelesen,
   * weil ein alter Server das Feld nicht kennt — dann gilt wie ueberall auf
   * diesem Pfad fail closed (siehe {@link availabilityOfFeature}).
   */
  readonly features?: readonly AccessFeatureState[];
}

/**
 * Die Zugangslage eines Features aus einer Antwort. Fehlt das Feature (alter
 * Server, Tippfehler im Namen), ist die Antwort `feature_unavailable`: die
 * geschlossene Richtung und die Lage, die die Oberflaeche ruhig darstellt.
 *
 * Review I 002989: dasselbe gilt fuer einen Wert, den dieser Client nicht
 * kennt (neuerer Server). Er wird hier auf `feature_unavailable` abgebildet,
 * statt ihn weiterzugeben — sonst landet die Oberflaeche in einem Zustand, fuer
 * den sie keinen Zweig hat, und zeigt gar nichts. Geschlossen und ruhig statt
 * offen oder leer.
 */
export function availabilityOfFeature(
  data: MeAccessResponse | undefined,
  feature: string,
): FeatureAvailability {
  const availability = data?.features?.find((f) => f.feature === feature)?.availability;
  if (availability === undefined || !KNOWN_AVAILABILITIES.includes(availability)) {
    return "feature_unavailable";
  }
  return availability;
}

/** Eine Abfrage, ein Cache-Eintrag (§7.4) — sie haengt an der Session, nicht an der Route. */
export const ME_ACCESS_QUERY_KEY = ["me", "access"] as const;

export interface Access {
  readonly can: (p: Permission) => boolean;
  /** Anzeigelabel der ersten erkannten Rolle, vom Server (§7.2). */
  readonly roleLabel: string | null;
  readonly modules: readonly AccessModuleState[];
  /** Laeuft die erste Abfrage noch? Bis dahin gewaehrt `can()` nichts. */
  readonly loading: boolean;
}

const NO_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>();

function accessOf(
  permissions: ReadonlySet<Permission>,
  roleLabel: string | null,
  modules: readonly AccessModuleState[],
  loading: boolean,
): Access {
  return { can: (p: Permission) => permissions.has(p), roleLabel, modules, loading };
}

/** Die fail-closed-Antwort: kein Recht, kein Label, keine Module. */
export function failClosedAccess(loading: boolean): Access {
  return accessOf(NO_PERMISSIONS, null, [], loading);
}

/** Reine Umsetzung der Serverantwort in die Lesesicht — ohne React, fuer Tests. */
export function accessFrom(data: MeAccessResponse | undefined, loading: boolean): Access {
  if (!data) return failClosedAccess(loading);
  return accessOf(new Set(data.permissions), data.roleLabel, data.modules, loading);
}

/**
 * Die EINE Abfrage — seit W4 Schritt 7 auch fuer die Modulsicht
 * (`modules/useModules.ts` fragt nichts mehr selbst):
 * 5 Minuten frisch, 30 Minuten im Speicher. `retry: false`, weil ein Fehler
 * hier nicht in eine lange Wiederholungskette laufen soll — die Antwort ist
 * fail closed und die naechste Navigation fragt erneut.
 */
export function useMeAccessQuery(): UseQueryResult<MeAccessResponse> {
  return useQuery<MeAccessResponse>({
    queryKey: ME_ACCESS_QUERY_KEY,
    queryFn: ({ signal }) => apiGet<MeAccessResponse>("/v1/me/access", undefined, signal),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Wird von `<AccessProvider>` gefuellt; `null` heisst „kein Provider montiert“. */
export const AccessContext = createContext<Access | null>(null);

/**
 * Ohne Provider: die Testbruecke (`lib/accessTestBridge.ts`) antwortet, wenn
 * sie gesetzt ist, sonst gilt fail closed. In Produktion ist sie nie gesetzt.
 */
function accessWithoutProvider(roles: readonly string[]): Access {
  const bridge = accessTestBridge();
  if (!bridge) return failClosedAccess(false);
  const bridged = bridge(roles);
  return accessOf(
    new Set(bridged.permissions as readonly Permission[]),
    bridged.roleLabel,
    bridged.modules as readonly AccessModuleState[],
    false,
  );
}

/**
 * Die Session wird weiter gelesen, aber nur noch fuer zwei Dinge: der Aufruf
 * ausserhalb eines `<SessionProvider>` soll wie bisher werfen, und die
 * Testbruecke braucht die Rollen als Schluessel. Nach aussen gibt dieser
 * Vertrag seit W4 Schritt 4 keine Rolle mehr heraus (§8), damit kein
 * `roles.includes(...)` auf dem Zugriffspfad nachwachsen kann.
 */
export function useAccess(): Access {
  const session = useSession();
  const ctx = useContext(AccessContext);
  const roles = session.roles;
  return useMemo(() => ctx ?? accessWithoutProvider(roles), [ctx, roles]);
}

/**
 * GT-REPORTS-2-PHASE1B-3 — non-throwing variant for components that only
 * ADAPT their rendering to the caller's permissions (e.g. an admin-only
 * detail line) and must keep rendering a least-privileged default when no
 * session context is mounted (mirror of `useOptionalSession`'s contract).
 * Without a session, `can()` is always false — the safe direction: an
 * extra permission is never granted, at worst an admin-only hint is hidden.
 */
export function useOptionalAccess(): Access {
  const session = useOptionalSession();
  const ctx = useContext(AccessContext);
  const roles = useMemo(() => session?.roles ?? [], [session]);
  return useMemo(() => ctx ?? accessWithoutProvider(roles), [ctx, roles]);
}
